import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, LogIn, LogOut, KeyRound, UserCog, UserCheck, Building2,
  FolderPlus, FolderMinus, GraduationCap, Activity, AlertTriangle, Loader2, RefreshCw,
  Layers, Megaphone, Package, Factory, Users, Award, Receipt, Landmark, Sparkles, Wrench, Boxes, Zap,
} from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { GitBranch } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// action -> {icon, tone, label(es/en)}
const ACTION_META = {
  "auth.login_success": { icon: LogIn, tone: "emerald", es: "Inicio de sesión", en: "Signed in" },
  "auth.login_failed": { icon: AlertTriangle, tone: "amber", es: "Intento fallido de acceso", en: "Failed login attempt" },
  "auth.login_locked": { icon: AlertTriangle, tone: "red", es: "Cuenta bloqueada (fuerza bruta)", en: "Account locked (brute-force)" },
  "auth.password_reset_requested": { icon: KeyRound, tone: "violet", es: "Solicitó restablecer contraseña", en: "Requested password reset" },
  "auth.password_reset": { icon: KeyRound, tone: "violet", es: "Restableció su contraseña", en: "Reset password" },
  "user.role_changed": { icon: UserCog, tone: "violet", es: "Cambió el rol de un usuario", en: "Changed a user's role" },
  "user.status_changed": { icon: UserCheck, tone: "emerald", es: "Cambió el estatus de un usuario", en: "Changed a user's status" },
  "tenant.created": { icon: Building2, tone: "gold", es: "Creó una empresa", en: "Created a company" },
  "tenant.settings_updated": { icon: Building2, tone: "violet", es: "Actualizó los ajustes del tenant", en: "Updated tenant settings" },
  "policy.updated": { icon: ShieldCheck, tone: "amber", es: "Actualizó las políticas de rol", en: "Updated role policies" },
  "marketplace.installed": { icon: Boxes, tone: "emerald", es: "Instaló del Marketplace", en: "Installed from Marketplace" },
  "marketplace.published": { icon: Boxes, tone: "violet", es: "Publicó en el Marketplace", en: "Published to Marketplace" },
  "extension.created": { icon: Boxes, tone: "emerald", es: "Creó un webhook", en: "Created a webhook" },
  "extension.field_defined": { icon: Boxes, tone: "violet", es: "Definió un campo personalizado", en: "Defined a custom field" },
  "integration.connected": { icon: Zap, tone: "emerald", es: "Conectó una integración", en: "Connected an integration" },
  "integration.disconnected": { icon: Zap, tone: "red", es: "Desconectó una integración", en: "Disconnected an integration" },
  "project.created": { icon: FolderPlus, tone: "emerald", es: "Creó un proyecto", en: "Created a project" },
  "project.deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un proyecto", en: "Deleted a project" },
  "academy.course_created": { icon: GraduationCap, tone: "violet", es: "Creó un curso en Academy", en: "Created an Academy course" },
  "academy.path_created": { icon: GraduationCap, tone: "violet", es: "Creó una ruta de aprendizaje", en: "Created a learning path" },
  "academy.certified": { icon: Award, tone: "gold", es: "Obtuvo un certificado", en: "Earned a certificate" },
  // GOÛT (Estudios)
  "gout.project_created": { icon: FolderPlus, tone: "emerald", es: "Creó un proyecto GOÛT", en: "Created a GOÛT project" },
  "gout.project_updated": { icon: Layers, tone: "violet", es: "Actualizó un proyecto GOÛT", en: "Updated a GOÛT project" },
  "gout.project_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un proyecto GOÛT", en: "Deleted a GOÛT project" },
  "gout.phase_changed": { icon: Layers, tone: "amber", es: "Cambió una fase en GOÛT", en: "Changed a GOÛT phase" },
  // TAGMA
  "tagma.campaign_created": { icon: Megaphone, tone: "emerald", es: "Creó una campaña TAGMA", en: "Created a TAGMA campaign" },
  "tagma.campaign_updated": { icon: Megaphone, tone: "violet", es: "Actualizó una campaña TAGMA", en: "Updated a TAGMA campaign" },
  "tagma.campaign_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó una campaña TAGMA", en: "Deleted a TAGMA campaign" },
  // VETTA
  "vetta.supplier_created": { icon: Factory, tone: "emerald", es: "Creó un proveedor VETTA", en: "Created a VETTA supplier" },
  "vetta.supplier_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un proveedor VETTA", en: "Deleted a VETTA supplier" },
  "vetta.product_created": { icon: Boxes, tone: "emerald", es: "Creó un producto VETTA", en: "Created a VETTA product" },
  "vetta.product_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un producto VETTA", en: "Deleted a VETTA product" },
  "vetta.order_created": { icon: Package, tone: "emerald", es: "Creó una orden de producción", en: "Created a production order" },
  "vetta.order_status_changed": { icon: Wrench, tone: "amber", es: "Cambió el estado de una orden", en: "Changed an order status" },
  "vetta.order_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó una orden VETTA", en: "Deleted a VETTA order" },
  // AURA
  "aura.collection_created": { icon: Sparkles, tone: "emerald", es: "Creó una colección AURA", en: "Created an AURA collection" },
  "aura.collection_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó una colección AURA", en: "Deleted an AURA collection" },
  "aura.product_created": { icon: Package, tone: "emerald", es: "Creó un producto AURA", en: "Created an AURA product" },
  "aura.product_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un producto AURA", en: "Deleted an AURA product" },
  // CRM
  "crm.lead_created": { icon: Users, tone: "emerald", es: "Creó un lead", en: "Created a lead" },
  "crm.lead_updated": { icon: Users, tone: "violet", es: "Actualizó un lead", en: "Updated a lead" },
  "crm.stage_changed": { icon: Users, tone: "amber", es: "Movió un lead de etapa", en: "Moved a lead's stage" },
  "crm.lead_deleted": { icon: FolderMinus, tone: "red", es: "Eliminó un lead", en: "Deleted a lead" },
  // Operaciones
  "ops.expense_added": { icon: Receipt, tone: "amber", es: "Registró un gasto operativo", en: "Added an operating expense" },
  // Contabilidad
  "accounting.invoice_uploaded": { icon: Receipt, tone: "violet", es: "Cargó una factura", en: "Uploaded an invoice" },
  "accounting.reconciled": { icon: Landmark, tone: "emerald", es: "Concilió un movimiento bancario", en: "Reconciled a bank movement" },
  // Workflow Platform (L6)
  "workflow.created": { icon: GitBranch, tone: "violet", es: "Creó un flujo de trabajo", en: "Created a workflow" },
  "workflow.updated": { icon: GitBranch, tone: "violet", es: "Actualizó un flujo de trabajo", en: "Updated a workflow" },
  "workflow.deleted": { icon: GitBranch, tone: "red", es: "Eliminó un flujo de trabajo", en: "Deleted a workflow" },
  "workflow.instance_started": { icon: GitBranch, tone: "emerald", es: "Inició un flujo de trabajo", en: "Started a workflow" },
  "workflow.transitioned": { icon: GitBranch, tone: "amber", es: "Avanzó un flujo de trabajo", en: "Advanced a workflow" },
  // AI Platform (L7)
  "ai.copilot_query": { icon: Sparkles, tone: "violet", es: "Consultó al Copiloto OMNIA", en: "Queried the OMNIA Copilot" },
  "ai.invoked": { icon: Sparkles, tone: "violet", es: "Invocó un servicio de IA", en: "Invoked an AI service" },
  "ai.copilot_error": { icon: AlertTriangle, tone: "red", es: "Error del Copiloto OMNIA", en: "OMNIA Copilot error" },
  // People Suite — Motor de Eventos (Cap. 63)
  "workforce.clock_in": { icon: LogIn, tone: "emerald", es: "Registró su entrada", en: "Clocked in" },
  "workforce.clock_out": { icon: LogOut, tone: "amber", es: "Registró su salida", en: "Clocked out" },
  "workforce.time_started": { icon: Activity, tone: "emerald", es: "Inició tiempo en un proyecto", en: "Started project time" },
  "workforce.time_stopped": { icon: Activity, tone: "amber", es: "Detuvo el tiempo de un proyecto", en: "Stopped project time" },
  "workforce.shift_created": { icon: Layers, tone: "violet", es: "Creó un turno", en: "Created a shift" },
  "workforce.timesheet_logged": { icon: Receipt, tone: "violet", es: "Registró un parte de horas", en: "Logged a timesheet" },
  "workforce.timesheet_reviewed": { icon: UserCheck, tone: "emerald", es: "Revisó un parte de horas", en: "Reviewed a timesheet" },
  "positions.created": { icon: Building2, tone: "emerald", es: "Creó un puesto", en: "Created a position" },
  "positions.assigned": { icon: UserCheck, tone: "violet", es: "Asignó un colaborador a un puesto", en: "Assigned an employee to a position" },
  "wfp.plan_created": { icon: Layers, tone: "emerald", es: "Creó un plan de plantilla", en: "Created a workforce plan" },
  "wfp.state_changed": { icon: GitBranch, tone: "amber", es: "Cambió el estado de un plan de plantilla", en: "Changed a workforce plan status" },
  "recruiting.job_created": { icon: FolderPlus, tone: "emerald", es: "Publicó una vacante", en: "Opened a vacancy" },
  "recruiting.candidate_created": { icon: Users, tone: "emerald", es: "Registró un candidato", en: "Added a candidate" },
  "recruiting.stage_changed": { icon: GitBranch, tone: "amber", es: "Movió un candidato de etapa", en: "Moved a candidate stage" },
  "recruiting.interview_scheduled": { icon: Users, tone: "violet", es: "Programó una entrevista", en: "Scheduled an interview" },
  "recruiting.offer_created": { icon: Award, tone: "violet", es: "Generó una oferta", en: "Created an offer" },
  "recruiting.offer_status": { icon: Award, tone: "amber", es: "Actualizó el estado de una oferta", en: "Updated an offer status" },
  "recruiting.hired": { icon: UserCheck, tone: "gold", es: "Contrató a un candidato", en: "Hired a candidate" },
  "approvals.request_created": { icon: ShieldCheck, tone: "violet", es: "Creó una solicitud de aprobación", en: "Created an approval request" },
  "approvals.request_decided": { icon: ShieldCheck, tone: "emerald", es: "Resolvió una aprobación", en: "Decided an approval" },
  "payroll.period_created": { icon: Receipt, tone: "violet", es: "Creó un periodo de nómina", en: "Created a payroll period" },
  "payroll.payslip_created": { icon: Receipt, tone: "emerald", es: "Generó un recibo de nómina", en: "Created a payslip" },
  "payroll.payslip_from_attendance": { icon: Receipt, tone: "violet", es: "Generó recibo desde asistencia", en: "Created payslip from attendance" },
  "payroll.payslip_issued": { icon: Landmark, tone: "gold", es: "Emitió un recibo de nómina", en: "Issued a payslip" },
  "payroll.payslip_adjusted": { icon: Receipt, tone: "amber", es: "Ajustó un recibo de nómina", en: "Adjusted a payslip" },
  "payroll.compensation_set": { icon: Landmark, tone: "violet", es: "Actualizó la compensación", en: "Set compensation" },
  "payroll.benefit_created": { icon: Award, tone: "emerald", es: "Creó un beneficio", en: "Created a benefit" },
  "lifecycle.event": { icon: UserCog, tone: "violet", es: "Registró un evento de ciclo de vida", en: "Logged a lifecycle event" },
  "people.member_invited": { icon: UserCheck, tone: "emerald", es: "Invitó a un colaborador", en: "Invited an employee" },
  "people.membership_updated": { icon: UserCog, tone: "violet", es: "Actualizó el expediente de un colaborador", en: "Updated an employee record" },
  "people.profile_updated": { icon: UserCog, tone: "violet", es: "Actualizó un perfil", en: "Updated a profile" },
  "performance.feedback_given": { icon: Award, tone: "violet", es: "Envió feedback", en: "Gave feedback" },
};

const TONE_CLS = {
  emerald: "bg-emerald-500/10 text-emerald-600",
  amber: "bg-amber-500/10 text-amber-600",
  red: "bg-destructive/10 text-destructive",
  violet: "bg-violet/10 text-violet",
  gold: "bg-gold/15 text-gold",
  slate: "bg-navy/5 text-navy/50",
};

function relTime(iso, lang) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  const es = lang === "es";
  if (s < 60) return es ? "hace instantes" : "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return es ? `hace ${m} min` : `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return es ? `hace ${h} h` : `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return es ? `hace ${days} d` : `${days}d ago`;
  return d.toLocaleDateString(es ? "es-MX" : "en-US");
}

function DiffBadge({ before, after }) {
  const keys = Object.keys({ ...(before || {}), ...(after || {}) });
  if (keys.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {keys.map((k) => {
        const b = before?.[k];
        const a = after?.[k];
        if (b !== undefined && a !== undefined && String(b) !== String(a))
          return <span key={k} className="text-[11px] px-2 py-0.5 rounded bg-navy/5 text-navy/60">{k}: <span className="line-through text-navy/40">{String(b)}</span> → <span className="text-navy/80">{String(a)}</span></span>;
        const v = a !== undefined ? a : b;
        return <span key={k} className="text-[11px] px-2 py-0.5 rounded bg-navy/5 text-navy/60">{k}: {String(v)}</span>;
      })}
    </div>
  );
}

export default function Bitacora() {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ events: [], total: 0, is_super: false, tenants: [], actions: [] });
  const [tenant, setTenant] = useState("all");
  const [action, setAction] = useState("all");
  const [limit, setLimit] = useState(60);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit) });
    if (tenant !== "all") params.set("tenant_id", tenant);
    if (action !== "all") params.set("action", action);
    api.get(`/audit?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenant, action, limit]);

  useEffect(() => { load(); }, [load]);

  const es = lang === "es";
  const meta = (a) => ACTION_META[a] || { icon: Activity, tone: "slate", es: a, en: a };

  return (
    <OSLayout title={es ? "Bitácora" : "Activity Log"}>
      <div className="space-y-8" data-testid="bitacora-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> OMNIA · Governance</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{es ? "Bitácora de actividad" : "Activity Log"}</h2>
            <p className="mt-2 text-navy/50">{es ? "Quién hizo qué y cuándo — línea de tiempo auditable de toda la organización." : "Who did what and when — an auditable timeline across the organization."}</p>
          </motion.div>
          <button data-testid="bitacora-refresh" onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {es ? "Actualizar" : "Refresh"}
          </button>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          {data.is_super && (
            <select data-testid="bitacora-filter-tenant" value={tenant} onChange={(e) => setTenant(e.target.value)}
              className="text-sm bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet">
              <option value="all">{es ? "Todas las empresas" : "All companies"}</option>
              {data.tenants.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
          )}
          <select data-testid="bitacora-filter-action" value={action} onChange={(e) => setAction(e.target.value)}
            className="text-sm bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet">
            <option value="all">{es ? "Todas las acciones" : "All actions"}</option>
            {data.actions.map((a) => <option key={a} value={a}>{es ? meta(a).es : meta(a).en}</option>)}
          </select>
          <span className="text-xs text-navy/40" data-testid="bitacora-count">{data.total} {es ? "eventos" : "events"}</span>
        </div>

        {/* Timeline */}
        <div className="bg-white border border-navy/10 rounded-lg" data-testid="bitacora-timeline">
          {loading && data.events.length === 0 ? (
            <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : data.events.length === 0 ? (
            <div className="py-16 text-center text-navy/40 text-sm">{es ? "Sin actividad registrada." : "No activity recorded."}</div>
          ) : (
            <ol className="relative">
              {data.events.map((e, i) => {
                const m = meta(e.action);
                const Icon = m.icon;
                return (
                  <motion.li key={e.id} data-testid={`bitacora-event-${e.id}`}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, delay: Math.min(i * 0.015, 0.3), ease }}
                    className="flex gap-4 px-5 py-4 border-b border-navy/5 last:border-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${TONE_CLS[m.tone]}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm">
                          <span className="font-medium">{e.actor_name}</span>{" "}
                          <span className="text-navy/60">{es ? m.es : m.en}</span>
                        </div>
                        <div className="text-[11px] text-navy/40 shrink-0" title={new Date(e.at).toLocaleString()}>{relTime(e.at, lang)}</div>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-navy/40">
                        <span className="font-mono">{e.resource}</span>
                        {data.is_super && <span className="px-1.5 py-0.5 rounded bg-navy/5">{e.tenant_name}</span>}
                      </div>
                      <DiffBadge before={e.before} after={e.after} />
                    </div>
                  </motion.li>
                );
              })}
            </ol>
          )}
        </div>

        {data.events.length < data.total && (
          <div className="flex justify-center">
            <button data-testid="bitacora-load-more" onClick={() => setLimit((l) => l + 60)}
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors">
              {es ? "Cargar más" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </OSLayout>
  );
}
